import {proxy} from "valtio";

import {type ThingToDoItem} from "@/entities/destination/attraction/types";

import {type IValtioModel} from "@/shared/models/base-model.interface";
import {Checkbox, type CheckboxInstance} from "@/shared/models/form/fields/checkbox";
import {Range, type RangeInstance} from "@/shared/models/form/fields/range";

export interface TagOption {
    value: string;
    label: string;
}

interface ThingsToDoConfig {
    tags: ReadonlyArray<TagOption>;
    items: ReadonlyArray<ThingToDoItem>;
    initialTag?: string;
    itemsPerPage?: number;
}

interface DurationModel {
    value: number;
    label: DurationLabelKey;
    model: CheckboxInstance;
}

type DurationLabelKey = "durations.under1Hour" | "durations.1to2Hours" | "durations.2to4Hours" | "durations.halfDay" | "durations.fullDay";

const DURATION_OPTIONS: ReadonlyArray<{value: number; label: DurationLabelKey}> = [
    {value: 1, label: "durations.under1Hour"},
    {value: 2, label: "durations.1to2Hours"},
    {value: 4, label: "durations.2to4Hours"},
    {value: 12, label: "durations.halfDay"},
    {value: 24, label: "durations.fullDay"},
] as const;

class ThingsToDoClass implements IValtioModel {
    // Observable state
    activeTags: Array<string> = [];
    durations: Array<number> = [];
    visibleCount: number;

    readonly priceModel: RangeInstance;
    readonly durationModels: Array<DurationModel>;

    private unsubscribe: Array<() => void> = [];

    readonly tags: ReadonlyArray<TagOption>;
    readonly items: ReadonlyArray<ThingToDoItem>;
    readonly itemsPerPage: number;

    constructor(config: ThingsToDoConfig) {
        this.tags = config.tags;
        this.items = config.items;
        this.activeTags = [];
        this.itemsPerPage = config.itemsPerPage || 8;
        this.visibleCount = this.itemsPerPage;

        // Initialize price range model (1-4 with step 0.5 for granularity)
        this.priceModel = Range({
            minValue: 1,
            maxValue: 4,
        });

        // Initialize duration filter models
        this.durationModels = DURATION_OPTIONS.map(({value, label}) => ({
            value: value,
            label,
            model: Checkbox({initialValue: false}),
        }));

        // Subscribe to filter changes
        this.initFilterSubscriptions();
    }

    private initFilterSubscriptions(): void {
        const handler = (): void => {
            const selectedDurations = this.durationModels.filter(({model}) => model.checked).map(({value}) => value);
            this.durations = selectedDurations;
            this.resetVisibleCount();
        };

        // Subscribe to price range changes (only on change, not input for better performance)
        // The change event fires when user releases the slider, avoiding excessive re-renders
        this.unsubscribe.push(
            this.priceModel.events.on("change", () => {
                this.resetVisibleCount();
            }),
        );

        // Subscribe to duration checkboxes
        this.durationModels.forEach(({model}) => {
            this.unsubscribe.push(model.events.on("change", handler));
        });
    }

    get filteredItems(): ReadonlyArray<ThingToDoItem> {
        return this.items.filter((item) => {
            // Tag filter: if no tags selected, show all; otherwise check if item has any of the selected tags
            const tagOk = this.activeTags.length === 0 ? true : item.tags?.some((tag) => this.activeTags.includes(tag));

            // Price filter: only filter if slider has been moved from default position
            const minPrice = this.priceModel.minValue !== null ? Math.round(this.priceModel.minValue) : 1;
            const maxPrice = this.priceModel.maxValue !== null ? Math.round(this.priceModel.maxValue) : 4;

            // Check if user has changed the default range
            const isPriceFilterActive = minPrice !== 1 || maxPrice !== 4;

            let priceOk = true;
            if (isPriceFilterActive) {
                // Treat null/undefined priceRange as 1 (cheapest)
                const itemPrice = item.priceRange ?? 1;
                priceOk = itemPrice >= minPrice && itemPrice <= maxPrice;
            }

            // Duration filter: if no durations selected, show all; otherwise check if item's duration matches any selected duration
            // Treat null/undefined duration as 1 (shortest duration)
            const durationOk = this.durations.length === 0 ? true : this.durations.includes(item.duration ?? 1);

            return Boolean(tagOk && priceOk && durationOk);
        });
    }

    get visibleItems(): ReadonlyArray<ThingToDoItem> {
        return this.filteredItems.slice(0, this.visibleCount);
    }

    get showLoadMoreButton(): boolean {
        return this.visibleCount < this.filteredItems.length;
    }

    get totalFilteredCount(): number {
        return this.filteredItems.length;
    }

    // Actions
    toggleTag(tag: string): void {
        if (!this.tags.some((t) => t.value === tag)) return;

        const index = this.activeTags.indexOf(tag);
        if (index > -1) {
            // Tag is active, remove it
            this.activeTags.splice(index, 1);
        } else {
            // Tag is not active, add it
            this.activeTags.push(tag);
        }
        this.resetVisibleCount();
    }

    isTagActive(tag: string): boolean {
        return this.activeTags.includes(tag);
    }

    loadMore(): void {
        this.visibleCount = Math.min(this.visibleCount + this.itemsPerPage, this.filteredItems.length);
    }

    resetVisibleCount(): void {
        // Simply reset to itemsPerPage, let visibleItems getter handle the actual slicing
        this.visibleCount = this.itemsPerPage;
    }

    reset(): void {
        this.activeTags = [];
        this.priceModel.setMinValue(1);
        this.priceModel.setMaxValue(4);
        this.durationModels.forEach(({model}) => {
            model.setChecked(false);
        });
        this.durations = [];
        this.resetVisibleCount();
    }

    dispose(): void {
        // Unsubscribe from all filter changes
        this.unsubscribe.forEach((unsubscribe) => unsubscribe());
        this.unsubscribe = [];
    }
}

// Export type
export type ThingsToDoInstance = ThingsToDoClass;

// Factory function
export function ThingsToDo(config: ThingsToDoConfig): ThingsToDoInstance {
    return proxy<ThingsToDoInstance>(new ThingsToDoClass(config));
}
